import { useCallback, useEffect, useRef, useState, type ReactNode, type Ref } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import { api, ErroreApi } from '../servizi/api.ts'
import { Attesa, Avviso, Intestazione } from '../componenti/Pezzi.tsx'
import { Bottone, Riquadro, Scelta, Spunta } from '../componenti/base.tsx'
import { IconaIndietro, IconaScarica } from '../componenti/Icone.tsx'
import type { SchedaLetta } from '../servizi/regole-anagrafica.ts'
import { giornoItaliano, type FirmatarioNoto, type ModuloConsensi } from '../servizi/regole-consensi.ts'
import {
  chiaveLogo, etichettaProgetto, indirizzoLogo, type BloccoFoglio, type Foglio, type LogoBreve, type PdfRichiesto,
  type ProgettoStampa,
} from '../servizi/stampa.ts'

/**
 * Stampa della scheda utente da firmare (D-10; modulo M03/I/SER/01): intestazione con Sede Servizio,
 * progetto, anno, mese e numero della scheda; le sezioni della scheda nell'ordine del modulo;
 * informativa sintetica, dichiarazione e quattro consensi con le caselle «Esprimo / Non esprimo»;
 * luogo, data e firma. Codice, revisione e testi arrivano dal catalogo dei moduli, non dal programma.
 *
 * Il foglio lo compone l'API (GET /persone/:id/stampa, servizi/foglio-scheda.ts); qui si sceglie e si
 * impagina. Le opzioni (segnalazione di CIDIS del 19/10/2026) sono tre: il progetto in intestazione,
 * quando la persona ha percorsi in piu' progetti; i loghi della fascia d'intestazione, preselezionati
 * quelli del progetto scelto piu' il logo CIDIS; la riga «Nome e qualità del firmatario» (decisione
 * del 18/10/2026), compilata se chi ha firmato l'ultima volta e' noto e chi stampa lo conferma.
 *
 * Due uscite con le stesse opzioni: la stampa del browser e il PDF/A-2b generato dal sistema
 * (POST /persone/:id/stampa/pdf), che il Job dei lavori prepara, sigilla e tiene sette giorni.
 *
 * Le caselle dei consensi si stampano sempre vuote, anche se i consensi sono gia' registrati: la
 * scelta la fa la persona sul foglio, e una casella gia' barrata non e' un consenso espresso. Il
 * foglio firmato si registra e si carica dalla sezione Consensi della scheda.
 *
 * L'impaginazione del browser e' solo CSS (@media print in stile-persona.css): pagina con nome,
 * numerazione «pag. X di Y» nei margini dove il browser la sa fare, comandi e navigazione esclusi.
 * L'apertura della pagina e' registrata dall'API come `persona.stampata`.
 *
 * La scheda da firmare e' il modulo dei consensi di CIDIS (495): dal riquadro «Moduli dei progetti»
 * si arriva con `?progetto=`, e il progetto in intestazione e' quello. Il foglio lo impagina
 * `FoglioSchedaUtente`, che usa anche «Stampa i due moduli» (StampaModuloProgetto.tsx).
 */

type DatiStampa = {
  scheda: SchedaLetta
  modulo: ModuloConsensi
  firmatario?: FirmatarioNoto | null
  riga_firmatario: string | null
  foglio: Foglio
  progetti: ProgettoStampa[]
  logo_cidis: LogoBreve
}

const NESSUN_PROGETTO = 'nessuno'
/** Attesa fra due richieste dell'elenco dei PDF mentre uno e' in preparazione, e per quanto al massimo. */
const OGNI_MS = 15_000
const PER_MS = 20 * 60_000

const messaggio = (e: unknown) => (e instanceof ErroreApi ? e.message
  : 'Non è andata a buon fine: controlla la connessione e riprova.')

const momento = (valore: string | null) => (valore
  ? new Date(valore).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '')

function VoceTestata({ nome, children }: { nome: string; children: ReactNode }) {
  return (
    <div className="persona-stampa-voce">
      <dt>{nome}</dt>
      <dd>{children ?? '—'}</dd>
    </div>
  )
}

function Blocco({ blocco: b }: { blocco: BloccoFoglio }) {
  switch (b.tipo) {
    case 'sottotitolo': return <p className="persona-sottotitolo">{b.testo}</p>
    case 'nota': return <p className="persona-nota-sezione">{b.testo}</p>
    case 'elenco': return <ul className="persona-voce-elenco">{b.voci.map((v, i) => <li key={i}>{v}</li>)}</ul>
    case 'dati':
      return (
        <dl className="persona-dati">
          {b.righe.map((r) => (
            <div className="dato" key={r.nome}>
              <dt>{r.nome}</dt>
              <dd>{r.valore === null ? <span className="tenue">—</span> : r.mono ? <span className="mono">{r.valore}</span> : r.valore}</dd>
            </div>
          ))}
        </dl>
      )
  }
}

/**
 * Il foglio della scheda utente da firmare, come lo compone l'API. Lo stampano questa pagina e «Stampa
 * i due moduli» (495), dopo il modulo del progetto. `progetto` e `riga` sono le scelte della pagina:
 * il progetto in intestazione e la riga del firmatario compilata, oppure nulla da scrivere a mano.
 */
export function FoglioSchedaUtente({ foglio, progetto, riga, loghi, fascia }: {
  foglio: Foglio
  progetto: string | null
  riga: string | null
  loghi: LogoBreve[]
  /** La pagina aspetta le immagini della fascia prima di stampare. */
  fascia?: Ref<HTMLDivElement>
}) {
  const t = foglio.testata
  return (
    <article className="persona-stampa" aria-label={`${foglio.modulo.titolo} da firmare`}>
      {loghi.length > 0 && (
        <div className="persona-stampa-loghi" ref={fascia}>
          {loghi.map((l) => <img key={chiaveLogo(l)} src={indirizzoLogo(l)} alt={l.nome} />)}
        </div>
      )}
      <header className="persona-stampa-testata">
        <div>
          <p className="persona-stampa-titolo">{foglio.modulo.titolo}</p>
          <p className="persona-stampa-revisione">
            {foglio.modulo.codice} · Rev {foglio.modulo.revisione} del {foglio.modulo.data_revisione}
          </p>
        </div>
        <dl className="persona-stampa-intestazione">
          <VoceTestata nome="Sede Servizio">{t.sede}</VoceTestata>
          <VoceTestata nome="Progetto">{progetto}</VoceTestata>
          <VoceTestata nome="Anno">{t.anno}</VoceTestata>
          <VoceTestata nome="Mese">{t.mese}</VoceTestata>
          <VoceTestata nome="Scheda SUS n.">{t.numero_scheda}</VoceTestata>
        </dl>
      </header>

      {foglio.sezioni.map((s) => (
        <section className="persona-stampa-sezione" key={s.titolo}>
          <h2>{s.titolo}</h2>
          {s.blocchi.map((b, i) => <Blocco key={i} blocco={b} />)}
        </section>
      ))}

      <section className="persona-stampa-sezione persona-stampa-consensi">
        <h2>{foglio.modulo.titolo_sezione}</h2>
        <p className="persona-stampa-informativa">{foglio.modulo.informativa}</p>
        <p>{foglio.modulo.dichiarazione}</p>
        <ol className="persona-stampa-dichiarazioni">
          {foglio.modulo.consensi.map((d) => (
            <li key={d.tipo}>
              <p className="persona-stampa-caselle">
                {d.facoltativo && <span>(Facoltativo)</span>}
                <span className="persona-stampa-casella" aria-hidden="true" /> Esprimo
                <span className="persona-stampa-casella" aria-hidden="true" /> Non esprimo
              </p>
              <p>{d.testo}{d.nota ? ` ${d.nota}` : ''}</p>
            </li>
          ))}
        </ol>
        <div className="persona-stampa-firma">
          <p className="persona-stampa-firmatario">
            Nome e qualità del firmatario{' '}
            {riga
              ? <span className="persona-stampa-compilata">{riga}</span>
              : <span className="persona-stampa-riga persona-stampa-riga-lunga" />}
            {!riga && (
              <span className="persona-stampa-aiuto">
                se firma un genitore, il tutore, l’amministratore di sostegno o il responsabile della struttura di
                accoglienza
              </span>
            )}
          </p>
          <p>Lì <span className="persona-stampa-riga" /></p>
          <p>Data <span className="persona-stampa-riga persona-stampa-riga-breve" /></p>
          <p>Firma <span className="persona-stampa-riga persona-stampa-riga-lunga" /></p>
        </div>
      </section>

      <footer className="persona-stampa-piede">{foglio.modulo.codice}</footer>
    </article>
  )
}

export function StampaScheda() {
  const { id = '' } = useParams()
  // Dal riquadro «Moduli dei progetti» (495): il progetto del modulo da firmare insieme.
  const progettoRichiesto = new URLSearchParams(useLocation().search).get('progetto')
  const [dati, setDati] = useState<DatiStampa | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [compila, setCompila] = useState(true)
  const [progetto, setProgetto] = useState<string>(NESSUN_PROGETTO)
  const [loghi, setLoghi] = useState<Set<string>>(new Set())
  const [pdf, setPdf] = useState<PdfRichiesto[] | null>(null)
  const [errorePdf, setErrorePdf] = useState<string | null>(null)
  const [richiesta, setRichiesta] = useState(false)
  const [esitoPdf, setEsitoPdf] = useState<string | null>(null)
  const fascia = useRef<HTMLDivElement>(null)
  const inizioAttesa = useRef<number | null>(null)

  /** Progetto scelto e loghi preselezionati: quelli del progetto piu' il logo CIDIS. */
  const scegliProgetto = useCallback((valore: string, d: DatiStampa) => {
    setProgetto(valore)
    const p = d.progetti.find((x) => x.id === valore)
    setLoghi(new Set([chiaveLogo(d.logo_cidis), ...(p?.loghi ?? []).map(chiaveLogo)]))
  }, [])

  useEffect(() => {
    let vivo = true
    api.get<DatiStampa>(`/persone/${id}/stampa`)
      .then((d) => {
        if (!vivo) return
        setDati(d)
        const richiesto = d.progetti.find((p) => p.id === progettoRichiesto)
        scegliProgetto(richiesto?.id ?? d.progetti[0]?.id ?? NESSUN_PROGETTO, d)
      })
      .catch((e) => {
        if (!vivo) return
        setErrore(e instanceof ErroreApi && e.stato === 404
          ? 'La scheda non si stampa con gli incarichi in corso: serve la lettura in chiaro della scheda.'
          : messaggio(e))
      })
    return () => { vivo = false }
  }, [id, scegliProgetto, progettoRichiesto])

  const caricaPdf = useCallback(() => {
    api.get<PdfRichiesto[]>(`/persone/${id}/stampa/pdf`)
      .then((r) => { setPdf(r); setErrorePdf(null) })
      .catch((e) => setErrorePdf(messaggio(e)))
  }, [id])
  useEffect(caricaPdf, [caricaPdf])

  // Mentre un PDF e' in preparazione l'elenco si aggiorna da solo, per venti minuti al massimo: poi
  // arriva comunque la notifica, e la pagina non tiene sveglio il database per niente.
  const inPreparazione = !!pdf?.some((p) => p.stato === 'in_preparazione')
  useEffect(() => {
    if (!inPreparazione) { inizioAttesa.current = null; return }
    inizioAttesa.current ??= Date.now()
    if (Date.now() - inizioAttesa.current > PER_MS) return
    const t = setTimeout(caricaPdf, OGNI_MS)
    return () => clearTimeout(t)
  }, [inPreparazione, pdf, caricaPdf])

  if (errore) return <Avviso tipo="errore">{errore}</Avviso>
  if (!dati) return <Attesa testo="Preparazione della stampa…" />

  const { foglio } = dati
  const riga = compila ? dati.riga_firmatario : null
  const scelto = dati.progetti.find((p) => p.id === progetto) ?? null
  const disponibili: LogoBreve[] = [dati.logo_cidis, ...(scelto?.loghi ?? [])]
  const inFascia = disponibili.filter((l) => loghi.has(chiaveLogo(l)))
  const t = foglio.testata

  const cambiaLogo = (chiave: string, si: boolean) => setLoghi((prima) => {
    const dopo = new Set(prima)
    if (si) dopo.add(chiave)
    else dopo.delete(chiave)
    return dopo
  })

  /** La stampa parte quando le immagini della fascia sono pronte: altrimenti il foglio esce senza loghi. */
  async function stampa() {
    const immagini = [...(fascia.current?.querySelectorAll('img') ?? [])]
    await Promise.all(immagini.map((i) => i.decode().catch(() => undefined)))
    window.print()
  }

  async function chiediPdf() {
    setRichiesta(true); setErrorePdf(null); setEsitoPdf(null)
    try {
      await api.post(`/persone/${id}/stampa/pdf`, {
        progetto_id: scelto?.id ?? null, loghi: inFascia.map((l) => l.id), firmatario: compila,
      })
      setEsitoPdf('Il PDF è in preparazione. Compare qui sotto quando è pronto e ti arriva una notifica; '
        + 'di solito ci vuole qualche minuto.')
      caricaPdf()
    } catch (e) { setErrorePdf(messaggio(e)) }
    finally { setRichiesta(false) }
  }

  async function scaricaPdf(p: PdfRichiesto) {
    setErrorePdf(null)
    try {
      const { url } = await api.get<{ url: string }>(`/persone/${id}/stampa/pdf/${p.id}/scarica`)
      window.open(url, '_blank', 'noopener')
    } catch (e) { setErrorePdf(messaggio(e)); caricaPdf() }
  }

  return (
    <>
      <div className="persona-non-stampa">
        <Intestazione
          titolo="Stampa della scheda utente"
          sottotitolo={<span className="mono">scheda n. {t.numero_scheda} · {dati.scheda.etichetta}</span>}
          azioni={(
            <>
              <Link className="bottone bottone-secondario" to={`/persone/${id}`}><IconaIndietro /> Torna alla scheda</Link>
              <Bottone variante="secondario" icona={<IconaScarica />} inCorso={richiesta} onClick={() => void chiediPdf()}>
                Scarica PDF
              </Bottone>
              <Bottone variante="primario" onClick={() => void stampa()}>Stampa</Bottone>
            </>
          )}
        />
        <p className="persona-nota-sezione">
          Anteprima del foglio da firmare. Le caselle dei consensi restano vuote: le barra la persona. Il foglio
          firmato si registra dalla sezione Consensi della scheda, con la scansione. L’apertura di questa pagina è
          registrata nella cronologia della scheda.
        </p>

        <Riquadro titolo="Opzioni di stampa">
          <div className="stampa-opzioni">
            {dati.progetti.length
              ? (
                <Scelta etichetta="Progetto in intestazione" valore={progetto} cambia={(v) => scegliProgetto(v, dati)}
                        opzioni={[
                          ...dati.progetti.map((p) => ({
                            valore: p.id, etichetta: `${etichettaProgetto(p)}${p.aperto ? '' : ' (percorsi chiusi)'}`,
                          })),
                          { valore: NESSUN_PROGETTO, etichetta: 'Nessun progetto' },
                        ]}
                        aiuto={dati.progetti.length > 1
                          ? 'La persona ha percorsi in più progetti: in intestazione ne va uno. È proposto quello del percorso aperto più recente.'
                          : 'È il progetto dei percorsi della persona.'} />
                )
              : <p className="persona-nota-sezione">La persona non ha percorsi in un progetto: l’intestazione resta senza progetto.</p>}

            <fieldset className="stampa-loghi-scelta">
              <legend>Loghi nell’intestazione</legend>
              {disponibili.map((l) => (
                <Spunta key={chiaveLogo(l)} valore={loghi.has(chiaveLogo(l))} cambia={(si) => cambiaLogo(chiaveLogo(l), si)}
                        etichetta={(
                          <span className="stampa-logo-voce">
                            <img src={indirizzoLogo(l)} alt="" />
                            {l.id === dati.logo_cidis.id ? `${l.nome} (logo CIDIS)` : l.nome}
                          </span>
                        )} />
              ))}
              {scelto && !scelto.loghi.length && (
                <p className="campo-aiuto">Il progetto non ha loghi: si aggiungono dalla pagina del progetto, sezione «Loghi nelle stampe».</p>
              )}
            </fieldset>

            {dati.riga_firmatario
              ? (
                <Spunta etichetta={<>Compila la riga «Nome e qualità del firmatario» con <strong>{dati.riga_firmatario}</strong></>}
                        valore={compila} cambia={setCompila}
                        aiuto={`È chi ha firmato i consensi l’ultima volta${dati.firmatario?.data
                          ? `, il ${giornoItaliano(dati.firmatario.data)}` : ''}. Se questa volta firma un’altra persona, `
                          + 'togli la spunta: la riga resta da compilare a mano.'} />
                )
              : (
                <p className="persona-nota-sezione">
                  La riga «Nome e qualità del firmatario» resta da compilare a mano: si scrive quando firma un genitore, il
                  tutore, l’amministratore di sostegno o il responsabile della struttura di accoglienza.
                </p>
                )}
          </div>
        </Riquadro>

        <Riquadro titolo="PDF/A generati dal sistema">
          <p className="persona-nota-sezione">
            «Scarica PDF» prepara un PDF/A, il formato per la conservazione, con le opzioni scelte qui sopra. Lo prepara il
            sistema in qualche minuto e ne registra l’impronta nel registro sigillato; resta disponibile per sette giorni
            e lo scarica solo chi l’ha chiesto.
          </p>
          {esitoPdf && <Avviso tipo="successo">{esitoPdf}</Avviso>}
          {errorePdf && <Avviso tipo="errore">{errorePdf}</Avviso>}
          {pdf === null ? <Attesa /> : !pdf.length ? <p className="tenue piccolo">Nessun PDF richiesto per questa scheda.</p> : (
            <ul className="stampa-pdf-elenco">
              {pdf.map((p) => (
                <li key={p.id}>
                  <span>
                    {momento(p.richiesto_il)}{p.progetto ? ` · ${p.progetto}` : ''}
                    {p.pagine ? ` · ${p.pagine} ${p.pagine === 1 ? 'pagina' : 'pagine'}` : ''}
                  </span>
                  {p.stato === 'pronto' && (
                    <Bottone variante="secondario" dimensione="piccolo" icona={<IconaScarica />} onClick={() => void scaricaPdf(p)}>
                      Scarica
                    </Bottone>
                  )}
                  {p.stato === 'in_preparazione' && <span className="tenue">In preparazione…</span>}
                  {p.stato === 'scaduto' && <span className="tenue">Non più disponibile</span>}
                  {p.stato === 'non_riuscito' && <span className="tenue">Non riuscito: chiedine uno nuovo</span>}
                </li>
              ))}
            </ul>
          )}
        </Riquadro>
      </div>

      <FoglioSchedaUtente foglio={foglio} progetto={scelto ? etichettaProgetto(scelto) : null} riga={riga}
                          loghi={inFascia} fascia={fascia} />
    </>
  )
}
